import { reactive, watch } from "vue";
import dayjs from "dayjs";
import {
  addPayPlanProposalApprover,
  commitEmployeePayPlan,
  createPayPlanProposal,
  getListofEmployeesV2,
  getPayPlanContext,
} from "@/api/employeeAPI";
import launchDarklyService from "@/services/launchdarkly";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { resolveReadinessEnabled } from "@components/utils/useReadinessEnabled";

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function employeeIdFromRow(row) {
  return row?.employee?.id || row?.id || null;
}

function resolveCurrencyId(currency) {
  return currency?.currency_id ?? currency?.id ?? null;
}

/**
 * Wizard `payBasis` ids are lowercase (FirstPayPlanWizard.PAY_BASIS); the
 * commit-pay-plan serializer's ChoiceField is the upper-case PayRange.PayBasis
 * enum (HOURLY / MONTHLY / ANNUAL), so the payload must translate (Rule A-22).
 */
const PAY_BASIS_TO_API = {
  hourly: "HOURLY",
  monthly: "MONTHLY",
  annual: "ANNUAL",
};

function resolvePayBasis(payBasis) {
  return PAY_BASIS_TO_API[String(payBasis || "").trim().toLowerCase()] || null;
}

/** pay-plan-context scenario token → the wizard's calculator mode. */
const SCENARIO_TO_CALCULATOR_MODE = {
  "pay-range-found": "range",
  "no-pay-range-subscription": "no-range",
  "no-primary-position": "no-position",
};

/** API pay-basis enum → the wizard's lowercase id + label. */
const PAY_BASIS_TO_WIZARD = {
  ANNUAL: { payBasis: "annual", payBasisLabel: "Annually" },
  MONTHLY: { payBasis: "monthly", payBasisLabel: "Monthly" },
  HOURLY: { payBasis: "hourly", payBasisLabel: "Hourly" },
};

function toNumberOrNull(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function mapAdjustedBand(band) {
  if (!band) return null;
  return {
    anchor0: toNumberOrNull(band.min),
    anchor50: toNumberOrNull(band.at50),
    anchor100: toNumberOrNull(band.at100),
    anchor150: toNumberOrNull(band.max),
  };
}

/**
 * Map the pay-plan-context response (ER-11961 / ER-12108) onto the wizard's
 * presentational view-model: calculator mode, pre-filled terms, the Readiness
 * flag, the disabled date ranges (Rule 2a) and the `payRange` the calculator
 * renders.
 *
 * The context endpoint does not yet return the pay range's location label or
 * job category, so the calculator header renders those blank — tracked as a
 * separate [BE] gap.
 */
export function mapPayPlanContextToWizard(context) {
  const calculatorMode =
    SCENARIO_TO_CALCULATOR_MODE[context?.scenario] || "no-range";
  const basis =
    PAY_BASIS_TO_WIZARD[String(context?.pay_basis || "").toUpperCase()] || null;
  const position = context?.primary_position || {};
  const base = {
    calculatorMode,
    payBasis: basis?.payBasis ?? null,
    payBasisLabel: basis?.payBasisLabel ?? null,
    // Country / Used Currency objects (ER-12333). The pickers render the value
    // object's `name`, so ids alone would render blank.
    country: context?.country || null,
    currency: context?.currency || null,
    readinessModuleEnabled: resolveReadinessEnabled(context),
    disabledRanges: (context?.effective_on_disabled_ranges || []).map(
      (range) => ({ from: range.effective_on, to: range.effective_by || null })
    ),
    nextConflictDate: context?.next_conflict_effective_on || null,
    defaultAmount: toNumberOrNull(context?.default_amount),
    defaultApprover: mapContextDefaultApprover(context?.default_approver),
    // Rules A-37 / B-21 (ER-10474): while a proposal/evaluation is in flight
    // the direct-create wizard must not submit. The context supplies the
    // reason and message; the backend enforces the same lock.
    blockedBy: context?.blocked_by || null,
  };
  if (calculatorMode !== "range") {
    return {
      ...base,
      payRange: position.code
        ? { positionCode: position.code, positionName: position.name || "" }
        : {},
    };
  }

  const range = context.pay_range || {};
  const band = context.pay_range_1fte || {};

  return {
    ...base,
    payRange: {
      positionCode: position.code || "",
      positionName: position.name || "",
      payRangeCode: range.code || "",
      payBasisCode: String(context?.pay_basis || "").toUpperCase(),
      anchor0: toNumberOrNull(band.min),
      anchor50: toNumberOrNull(band.at50),
      anchor100: toNumberOrNull(band.at100),
      anchor150: toNumberOrNull(band.max),
      adjusted: mapAdjustedBand(context.pay_range_adjusted),
      fteAdjustmentApplied: Boolean(context.fte_adjustment_applied),
      allocationFte: toNumberOrNull(context.allocation_fte),
      rangeMin: toNumberOrNull(range.min_amount),
      rangeMax: toNumberOrNull(range.max_amount),
      versionNumber: range.version_number ?? null,
      versionAsOf: range.version_effective_from || "",
      currentReadinessPct: context.current_readiness_pct ?? null,
      fairPayAmount: toNumberOrNull(context.fair_pay_amount),
      tolerancePct: toNumberOrNull(context.fair_pay_tolerance_pct),
      // Rule A-40: where the amount sits in the range, computed by the server.
      rangePenetration: context.range_penetration || null,
    },
  };
}

export const DEFAULT_APPROVER_ROLE_LABEL = "Department Manager · default approver";

function initialsForName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function mapApproverItem(employee) {
  const first = employee?.first_name || "";
  const last = employee?.last_name || "";
  const name = `${first} ${last}`.trim() || employee?.name || "";
  return {
    id: employee.id,
    name,
    initials: `${first[0] || ""}${last[0] || ""}`.toUpperCase() || undefined,
  };
}

/**
 * Step 3's auto-resolved Department Manager row (ER-10995).
 *
 * Backend handoff: `GET .../pay-plan-context/` will publish
 * `default_approver: { employee_id, name } | null`, resolved by the same
 * department-head walk the approval engine runs on create. The field is
 * optional here, so the wizard keeps its current "pre-approved by you" state
 * until that endpoint ships and then renders the default row automatically.
 */
export function mapContextDefaultApprover(approver) {
  const employeeId = approver?.employee_id ?? approver?.id ?? null;
  if (employeeId == null) return null;
  const name = String(approver?.name || "").trim();
  return {
    id: employeeId,
    name,
    initials: initialsForName(name),
    roleLabel: DEFAULT_APPROVER_ROLE_LABEL,
    isDefault: true,
  };
}

function emptyWizardForm() {
  return {
    step: 1,
    submitting: false,
    effectiveDate: dayjs().format("YYYY-MM-DD"),
    country: null,
    currency: null,
    effectiveBy: null,
    baseSalary: null,
    payBasis: "annual",
    payBasisLabel: "Annually",
    readinessModuleEnabled: true,
    disabledRanges: [],
    nextConflictDate: null,
    defaultAmount: null,
    blockedBy: null,
    effectiveToEnabled: false,
    calculatorMode: "no-position",
    payRange: {},
    approvers: [],
    approverItems: [],
    approverHasMore: false,
    approverLoading: false,
  };
}

export function buildPayPlanWritePayload(form) {
  return {
    amount: String(form.baseSalary),
    currency_id: resolveCurrencyId(form.currency),
    operating_country_id: form.country?.id,
    effective_date: form.effectiveDate,
    pay_basis: resolvePayBasis(form.payBasis),
    ...(form.effectiveBy ? { effective_by: form.effectiveBy } : {}),
  };
}

export function buildPayPlanProposalPayload(form) {
  const amount = Number(form.baseSalary);
  return {
    employee: form.employeeId,
    effective_date: form.effectiveDate,
    proposed_amount: Number.isFinite(amount) ? amount.toFixed(2) : null,
    proposed_currency: resolveCurrencyId(form.currency),
    proposed_operating_country: form.country?.id,
  };
}

function buildReadyPayload(wizard) {
  const accountId = getAccountId();
  const payload = buildPayPlanWritePayload(wizard);
  if (
    !accountId ||
    !wizard.employeeId ||
    !payload.currency_id ||
    !payload.operating_country_id ||
    !payload.pay_basis
  ) {
    return null;
  }
  return { accountId, payload };
}

function buildProposalReadyPayload(wizard) {
  const accountId = getAccountId();
  const payload = buildPayPlanProposalPayload(wizard);
  if (
    !accountId ||
    !payload.employee ||
    !payload.effective_date ||
    !payload.proposed_amount ||
    !payload.proposed_currency ||
    !payload.proposed_operating_country
  ) {
    return null;
  }
  return { accountId, payload };
}

function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.details === "string" && data.details.trim()) return data.details;
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  const first = data && Object.values(data).find((value) => value != null);
  if (Array.isArray(first) && first[0]) return String(first[0]);
  if (typeof first === "string" && first.trim()) return first;
  return fallback;
}

/**
 * Host state for Add First Pay Plan (contract) and Propose Pay Plan (proposal) wizards.
 */
export function usePayPlanWizards({ fetchRows, proxy } = {}) {
  const wizard = reactive({
    type: null,
    employeeId: null,
    ...emptyWizardForm(),
  });

  function reset() {
    clearTimeout(penetrationTimer);
    Object.assign(wizard, { type: null, employeeId: null, ...emptyWizardForm() });
  }

  // Guards against a stale context response overwriting a newer one (the
  // effective date can change while a request is in flight).
  let contextRequestId = 0;

  /**
   * Resolve Step 2's pay-range lookup + Fair Pay context (ER-11961 / ER-10994).
   * Without this the wizard is told "no-range" for every employee with a
   * position title — even when a pay range is in force at the effective date.
   */
  async function loadPayPlanContext() {
    const accountId = getAccountId();
    const { employeeId, effectiveDate } = wizard;
    if (!accountId || !employeeId || !effectiveDate) return;

    const requestId = (contextRequestId += 1);
    try {
      const response = await getPayPlanContext(accountId, employeeId, {
        effective_date: effectiveDate,
      });
      // Drop the result if the wizard closed or a newer request superseded it.
      if (requestId !== contextRequestId || !wizard.type) return;
      const {
        calculatorMode,
        payRange,
        payBasis,
        payBasisLabel,
        country,
        currency,
        readinessModuleEnabled,
        disabledRanges,
        nextConflictDate,
        defaultAmount,
        defaultApprover,
        blockedBy,
      } = mapPayPlanContextToWizard(response?.data);
      wizard.calculatorMode = calculatorMode;
      wizard.payRange = payRange;
      wizard.readinessModuleEnabled = readinessModuleEnabled;
      wizard.disabledRanges = disabledRanges;
      wizard.nextConflictDate = nextConflictDate;
      wizard.defaultAmount = defaultAmount;
      wizard.blockedBy = blockedBy;
      // ER-10995: keep the auto-resolved Department Manager row first while
      // preserving any approvers the admin added in Step 3 across context
      // refreshes (the effective date can change while the wizard is open).
      const adminApprovers = wizard.approvers.filter((row) => !row.isDefault);
      wizard.approvers = defaultApprover
        ? [defaultApprover, ...adminApprovers]
        : adminApprovers;
      if (payBasis) {
        wizard.payBasis = payBasis;
        wizard.payBasisLabel = payBasisLabel;
      }
      if (country) wizard.country = country;
      if (currency) wizard.currency = currency;
      if (!readinessModuleEnabled && calculatorMode === "range") refreshPenetration();
      // Rule 3b: Base Salary defaults to Fair Pay at current readiness, only
      // with the Readiness module and a range, and never over a typed value.
      const salaryEmpty = wizard.baseSalary == null || wizard.baseSalary === "";
      if (
        salaryEmpty &&
        calculatorMode === "range" &&
        readinessModuleEnabled &&
        defaultAmount != null
      ) {
        wizard.baseSalary = defaultAmount;
      }
    } catch {
      // Keep the on-open heuristic so a context failure can't break the wizard.
    }
  }

  // Rule A-40: with Readiness off the server places the typed amount in the range
  // (`position_pct`), so the bar needs a fresh context per amount.
  let penetrationRequestId = 0;
  let penetrationTimer = null;
  const PENETRATION_DEBOUNCE_MS = 400;

  async function refreshPenetration() {
    const accountId = getAccountId();
    const { employeeId, effectiveDate, baseSalary } = wizard;
    const amount = Number(baseSalary);
    if (!accountId || !employeeId || !effectiveDate) return;
    if (wizard.readinessModuleEnabled || wizard.calculatorMode !== "range") return;
    if (!Number.isFinite(amount) || amount <= 0) return;

    const requestId = (penetrationRequestId += 1);
    try {
      const response = await getPayPlanContext(accountId, employeeId, {
        effective_date: effectiveDate,
        amount,
      });
      if (requestId !== penetrationRequestId || !wizard.type) return;
      wizard.payRange = {
        ...wizard.payRange,
        rangePenetration: response?.data?.range_penetration || null,
      };
    } catch {
      // Keep the last placement; the bar degrades to its neutral state.
    }
  }

  function schedulePenetrationRefresh() {
    clearTimeout(penetrationTimer);
    penetrationTimer = setTimeout(refreshPenetration, PENETRATION_DEBOUNCE_MS);
  }

  watch(() => wizard.baseSalary, schedulePenetrationRefresh);

  // Step 2 re-resolves when the effective date changes (Rule 3a / Rule 12).
  watch(
    () => wizard.effectiveDate,
    () => {
      if (!wizard.type) return;
      // Effective to can never precede Effective from (Rule 2a).
      if (wizard.effectiveBy && wizard.effectiveBy < wizard.effectiveDate) {
        wizard.effectiveBy = null;
      }
      loadPayPlanContext();
    }
  );

  // Effective to ships behind idea-MDP-429.
  async function loadEffectiveToFlag() {
    try {
      const enabled =
        (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_429, false)) === true;
      if (wizard.type) wizard.effectiveToEnabled = enabled;
    } catch {
      wizard.effectiveToEnabled = false;
    }
  }

  const APPROVER_PAGE_SIZE = 10;
  let approverOffset = 0;
  let approverSearch = "";

  /**
   * Paginated approver search (10 at a time, like the Employees page). `append`
   * fetches the next page for the picker's scroll pagination; a fresh call resets
   * to page 1. `approverHasMore` mirrors the API's `next`.
   */
  async function loadApproverItems(excludeId, { append = false } = {}) {
    const accountId = getAccountId();
    if (!accountId) return;
    if (!append) approverOffset = 0;
    wizard.approverLoading = true;
    try {
      const response = await getListofEmployeesV2({
        account_id: accountId,
        queryParamsObj: {
          limit: APPROVER_PAGE_SIZE,
          offset: approverOffset,
          // Approvers must be able to sign in — only employees with an
          // operational access can be named (add-approver rejects others).
          has_access: true,
          ...(approverSearch ? { search: approverSearch } : {}),
        },
      });
      const mapped = (response?.data?.results || [])
        .filter((employee) => String(employee.id) !== String(excludeId))
        .map(mapApproverItem);
      wizard.approverItems = append ? [...wizard.approverItems, ...mapped] : mapped;
      wizard.approverHasMore = Boolean(response?.data?.next);
      approverOffset += APPROVER_PAGE_SIZE;
    } catch {
      if (!append) wizard.approverItems = [];
      wizard.approverHasMore = false;
    } finally {
      wizard.approverLoading = false;
    }
  }

  function searchApprovers(query) {
    approverSearch = String(query || "").trim();
    return loadApproverItems(wizard.employeeId, { append: false });
  }

  function loadMoreApprovers() {
    if (!wizard.approverHasMore || wizard.approverLoading) return Promise.resolve();
    return loadApproverItems(wizard.employeeId, { append: true });
  }

  function openForRow(type, row) {
    const employeeId = employeeIdFromRow(row);
    Object.assign(wizard, {
      ...emptyWizardForm(),
      type,
      employeeId,
      calculatorMode: row?.employee?.positionTitle ? "no-range" : "no-position",
    });
    // Resolve the real pay-range context (Step 2 + Rule 12); the heuristic
    // above only stands until the fetch resolves.
    loadPayPlanContext();
    if (type === "contract") loadEffectiveToFlag();
    if (type === "proposal" || type === "changes" || type === "evaluation") {
      loadApproverItems(employeeId);
    }
  }

  function openAddPlan(row) {
    openForRow("contract", row);
  }

  function openProposePlan(row) {
    openForRow("proposal", row);
  }

  function openProposeChanges(row) {
    openForRow("changes", row);
  }

  function openEvaluateEligibility(row) {
    openForRow("evaluation", row);
  }

  function close() {
    reset();
  }

  function goNext() {
    wizard.step += 1;
  }

  function goBack() {
    if (wizard.step > 1) wizard.step -= 1;
  }

  function addApprover(approver) {
    if (!approver?.id) return;
    if (wizard.approvers.some((row) => row.id === approver.id)) return;
    wizard.approvers = [...wizard.approvers, approver];
  }

  function removeApprover(id) {
    wizard.approvers = wizard.approvers.filter((row) => row.id !== id);
  }

  async function submitContract() {
    // ER-10474 / Rule B-2: never submit a direct-create pay plan while a
    // proposal or evaluation is already in flight for this employee.
    if (wizard.blockedBy) {
      if (proxy?.$notifications?.error) {
        proxy.$notifications.error(
          wizard.blockedBy.message ||
            "This employee already has a pay change in flight. Resolve it before adding a pay plan."
        );
      }
      return;
    }
    wizard.submitting = true;
    try {
      const ready = buildReadyPayload(wizard);
      if (!ready) {
        if (proxy?.$notifications?.error) {
          proxy.$notifications.error("Complete required fields before creating the pay plan");
        }
        return;
      }
      await commitEmployeePayPlan(ready.accountId, wizard.employeeId, ready.payload);
      if (proxy?.$notifications?.success) {
        proxy.$notifications.success("Pay plan created");
      }
      close();
      if (fetchRows) await fetchRows();
    } catch (error) {
      if (proxy?.$notifications?.error) {
        proxy.$notifications.error(apiErrorMessage(error, "Could not create pay plan"));
      }
    } finally {
      wizard.submitting = false;
    }
  }

  async function submitProposal() {
    if (wizard.type === "evaluation") {
      wizard.submitting = true;
      try {
        if (proxy?.$notifications?.success) {
          proxy.$notifications.success("Eligibility evaluation saved as draft");
        }
        close();
        if (fetchRows) await fetchRows();
      } finally {
        wizard.submitting = false;
      }
      return;
    }

    wizard.submitting = true;
    try {
      const ready = buildProposalReadyPayload(wizard);
      if (!ready) {
        if (proxy?.$notifications?.error) {
          proxy.$notifications.error("Complete required fields before saving the proposal");
        }
        return null;
      }
      const response = await createPayPlanProposal(ready.accountId, ready.payload);
      const proposal = response?.data || null;
      // ER-10995: the default department-manager task is created by the
      // approval engine on save; admin-added rows are not part of the create
      // payload, so persist them through the draft proposal's add-approver
      // action before closing. The auto-resolved default row is excluded —
      // the backend already resolved it.
      const adminApprovers = wizard.approvers.filter(
        (row) => row && !row.isDefault && row.id != null
      );
      const proposalId = proposal?.id ?? proposal?.proposal_id ?? null;
      if (proposalId != null && adminApprovers.length) {
        const failures = [];
        for (const approver of adminApprovers) {
          try {
            await addPayPlanProposalApprover(ready.accountId, proposalId, {
              employee_id: approver.id,
            });
          } catch (error) {
            failures.push(
              `${approver.name || approver.id}: ${apiErrorMessage(
                error,
                "could not be added"
              )}`
            );
          }
        }
        if (failures.length && proxy?.$notifications?.error) {
          proxy.$notifications.error(
            `Proposal saved as draft, but some approvers could not be added — ${failures.join("; ")}`
          );
        } else if (proxy?.$notifications?.success) {
          proxy.$notifications.success("Pay plan proposal saved as draft");
        }
      } else if (proxy?.$notifications?.success) {
        proxy.$notifications.success("Pay plan proposal saved as draft");
      }
      close();
      if (fetchRows) await fetchRows();
      return proposal;
    } catch (error) {
      if (proxy?.$notifications?.error) {
        proxy.$notifications.error(
          apiErrorMessage(error, "Could not save pay plan proposal")
        );
      }
      return null;
    } finally {
      wizard.submitting = false;
    }
  }

  return {
    wizard,
    openAddPlan,
    openProposePlan,
    openProposeChanges,
    openEvaluateEligibility,
    close,
    goNext,
    goBack,
    addApprover,
    removeApprover,
    searchApprovers,
    loadMoreApprovers,
    submitContract,
    submitProposal,
  };
}